import React, { useContext, useState } from 'react';
import ReactDOM from 'react-dom';
import { TABTestItem } from '../types/ab';
import { useNavigate } from "react-router";
import { capitalizeFirstLetter, compareUsernames, convertToDateMonthYear, mapAppletToWidget } from '../utils/ABHelper';
import { DeleteIcon, EditIcon, OverFlowIcon } from 'assests/GmsIcons';
import CustomChip from './CustomChip';
import { COMPLETED_STATUS, DELETE, DRAFT_STATUS, EDIT, SCHEDULED_STATUS } from '../utils/Constants';
import { ABContext } from '../contexts/ABContext';
import ResultsList from './ResultsList';
import AuthManager from 'Common/AuthManager';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Table from 'Components/DesignSystem/Table/Table';
import Menu from 'Components/DesignSystem/Menu/Menu';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { TableColumn } from 'types/DesignSystem/Table';
import { MenuItem } from 'types/DesignSystem/Menu';

type TABTableProps = {
    abTests: TABTestItem[];
    setDeleteTest: React.Dispatch<React.SetStateAction<boolean>>;
    setDeleteItem: React.Dispatch<React.SetStateAction<{ id: string, name: string }>>;
    deletePermission: boolean;
    updatePermission: boolean;
    /** Renders the DS Table's own skeleton rows instead of a separate full-width element. */
    loading?: boolean;
}

/**
 * The A/B test list grid, on the design-system `Table` (`okf/design-system/e2e.md`
 * § 141) — the same recipe as `ISRProjectTable` (§ 87) and the Lists grid (§ 81).
 *
 * **Every gate is byte-identical**: `hasEditPermission` / `hasDeletePermission` still
 * combine status + `ownership.userid === getUserId()` + the permission flag, and
 * `onEdit` / `onDelete` still redirect to permission-denied first and then reject a
 * non-owner with the same two toast strings. Test-name click still navigates to the
 * result page only for `IN_PROGRESS` / `COMPLETED`.
 *
 * What the hand-rolled table did that this stops doing:
 *
 * - **Two `<th>`s shared `id="result-header"`** — Result and Models. Duplicate ids are
 *   invalid HTML, and `document.getElementById` returned the first for both.
 * - **`tw-w-screen` on the `<table>`** sized the grid to the VIEWPORT, not to its
 *   container, so it always overflowed by exactly the sidebar's width no matter how
 *   little data was in it. The horizontal scrollbar on this page was an artefact of
 *   that, not of the column count.
 * - **A hand-rolled sticky first column with no opaque ground.** `tw-sticky tw-left-0`
 *   plus a per-row `tw-bg-[#F4F5F7]`/`tw-bg-white`, which is the exact defect § 106
 *   documents: a pinned cell composited over its scrolled-away neighbours. It held
 *   together only because the stripe colour was hardcoded on the cell itself; the DS
 *   `sticky: 'left'` derives the resolved row ground and keeps the hover state.
 * - **A `mousedown` + capture-phase `scroll` listener pair** to close the overflow
 *   menu. `Menu`'s `onDismiss` does outside-click and Escape; Escape is new — the
 *   legacy menu could not be dismissed from the keyboard at all.
 * - **`role="menu"` once PER item** (`OverflowOption` wrapped each row in its own
 *   menu), and "disabled" expressed as `opacity-50` + `pointer-events-none` on a
 *   `<Link to="#">` — visually dead but still in the tab order, announcing as a link.
 *   `MenuItem.disabled` is real disabled semantics.
 */
const ABTable = ({ abTests, setDeleteTest, setDeleteItem, deletePermission, updatePermission, loading = false }: TABTableProps) => {
    const navigate = useNavigate();
    /* Right-anchored, like § 81/§ 87: the menu hangs leftward from the kebab. */
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
    const [showIndex, setShowIndex] = useState<number | null>(null);
    const abContext = useContext(ABContext);

    const onDelete = (row: TABTestItem) => {
        setShowIndex(null);
        if (!deletePermission) { navigate("/workspace/module/permission/denied"); return; }

        if (!compareUsernames(row?.ownership?.username, getUserName())) {
            toast.error("Deletion restricted to test owner");
            return;
        }
        setDeleteItem({ id: row?.id, name: row?.name }); setDeleteTest(true);
    }

    const onEdit = (row: TABTestItem) => {
        setShowIndex(null);
        if (!updatePermission) { navigate("/workspace/module/permission/denied"); return; }
        if (!compareUsernames(row?.ownership?.username, getUserName())) {
            toast.error("Editing restricted to test owner");
            return;
        }
        navigateToDetailPage(row.name, row.id);
    }

    const handleOverflowClick = (event: React.MouseEvent<HTMLButtonElement>, rowIndex: number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({ top: rect.bottom + 2, right: window.innerWidth - rect.right });
        setShowIndex(prev => (prev === rowIndex ? null : rowIndex));
    }

    const navigateToDetailPage = (name: string, testId: string = '') => {
        abContext.clearData();
        navigate(`/gms/ab/detail/${testId}`);
    }
    const navigateToResultPage = (name: string, testId: string = '') => {
        abContext.clearData();
        navigate(`/gms/ab/result/${testId}`);
    }

    const getUserName = () => {
        let userObj = null;
        if (AuthManager.isAuthenticated()) userObj = AuthManager.getAuthUser();
        if (!userObj?.profile) return capitalizeFirstLetter("guest");
        return capitalizeFirstLetter(userObj.profile.first_name ?? '');
    };

    const getUserId = () => {
        const userobj = AuthManager.getAuthUser();
        const userProfile = userobj?.profile;
        const userid = userProfile?.id;
        return userid
    };

    const hasEditPermission = (row: TABTestItem) => [SCHEDULED_STATUS, DRAFT_STATUS].includes(row?.status) && row?.ownership?.userid === getUserId() && updatePermission;
    const hasDeletePermission = (row: TABTestItem) => [SCHEDULED_STATUS, DRAFT_STATUS, COMPLETED_STATUS].includes(row?.status) && row?.ownership?.userid === getUserId() && deletePermission;

    /** A test name is a link to its results only once there ARE results. */
    const isResultViewable = (row: TABTestItem) => row?.status === "IN_PROGRESS" || row?.status === "COMPLETED";

    const renderRowActions = (row: TABTestItem, rowIndex: number) => {
        const rowKey = row?.id ?? rowIndex;
        const menuItems: MenuItem[] = [
            {
                id: 'edit', label: EDIT, icon: <EditIcon />,
                disabled: !hasEditPermission(row),
                'data-testid': `ab-test-row-${rowIndex}-menu-btn-edit`,
                onClick: () => onEdit(row),
            },
            {
                id: 'delete', label: DELETE, icon: <DeleteIcon />,
                disabled: !hasDeletePermission(row),
                'data-testid': `ab-test-row-${rowIndex}-menu-btn-delete`,
                onClick: () => onDelete(row),
            },
        ];

        return (
            <>
                <button
                    type="button"
                    aria-label="More options"
                    aria-haspopup="menu"
                    aria-expanded={showIndex === rowIndex}
                    data-testid={`ab-row-overflow-${rowKey}`}
                    id={`overflow-menu-trigger-${rowIndex}`}
                    className="tw-cursor-pointer tw-rounded-ctl tw-p-1 hover:tw-bg-surface-hover-strong"
                    onClick={(e) => handleOverflowClick(e, rowIndex)}
                >
                    <OverFlowIcon />
                </button>
                {/*
                  Portalled to `body`: the DS Table's wrapper is an overflow container
                  (that is what makes the pinned column work), so an absolutely
                  positioned popover inside it would be clipped. Same arrangement as
                  § 81 / § 87.
                */}
                {showIndex === rowIndex ? ReactDOM.createPortal(
                    <div
                        id={`ab-test-row-${rowIndex}-menu`}
                        className="tw-fixed tw-z-[9999] tw-w-52"
                        style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                    >
                        <Menu
                            variant="compact"
                            items={menuItems}
                            data-testid={`ab-row-overflow-menu-${rowKey}`}
                            onDismiss={() => setShowIndex(null)}
                        />
                    </div>,
                    document.body
                ) : null}
            </>
        );
    };

    const columns: TableColumn<TABTestItem>[] = [
        {
            id: 'name',
            header: 'Test name',
            accessor: (r) => r?.name ?? '-',
            width: '18%',
            /* The pin the legacy markup hand-rolled — see § 106 for why doing it by
               hand leaves the cell transparent over its scrolled-away neighbours. */
            sticky: 'left',
            render: (_value, row, rowIndex) => (
                isResultViewable(row) ? (
                    <div
                        role="button"
                        tabIndex={0}
                        id={`test-name-${rowIndex}`}
                        data-testid={`components-abtable-td-1-${row?.id}`}
                        className="tw-text-accent-bold hover:tw-underline tw-cursor-pointer tw-truncate"
                        title={row?.name ?? ''}
                        onClick={() => navigateToResultPage(row?.name ?? '', row?.id ?? '')}
                        /* The legacy cell was a click-only `<td>` — not focusable, not
                           reachable by keyboard, with no indication it was actionable
                           beyond a colour. */
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigateToResultPage(row?.name ?? '', row?.id ?? '');
                            }
                        }}
                    >
                        {row?.name ?? '-'}
                    </div>
                ) : (
                    <div
                        id={`test-name-${rowIndex}`}
                        data-testid={`components-abtable-td-1-${row?.id}`}
                        className="tw-truncate"
                        title={row?.name ?? ''}
                    >
                        {row?.name ?? '-'}
                    </div>
                )
            ),
        },
        {
            id: 'applets',
            header: 'Widgets',
            accessor: (r) => mapAppletToWidget(r?.applets?.[0]?.applet_name ?? ''),
            width: '12%',
            render: (_value, row, rowIndex) => (
                <div id={`applets-${rowIndex}`}>
                    <Typography variant="body-primary" as="div" id={`applet-name-${rowIndex}`} className="tw-text-ink">
                        {mapAppletToWidget(row?.applets?.[0]?.applet_name ?? '')}
                    </Typography>
                    <Typography variant="caption-metadata" as="div" id={`applet-id-${rowIndex}`} className="tw-text-ink-tertiary">
                        {row?.applets?.[0]?.applet ?? ''}
                    </Typography>
                </div>
            ),
        },
        {
            id: 'status',
            header: 'Status',
            accessor: (r) => r?.status ?? '',
            width: '10%',
            render: (_value, row, rowIndex) => (
                <span id={`status-${rowIndex}`}>
                    <CustomChip status={row?.status ?? ''} data-testid={`ab-row-status-${row?.id ?? rowIndex}`} />
                </span>
            ),
        },
        {
            id: 'test_result',
            header: 'Result',
            accessor: (r) => r?.test_result ?? '-',
            width: '14%',
            render: (value, _row, rowIndex) => <span id={`test-result-${rowIndex}`}>{value}</span>,
        },
        {
            id: 'models',
            header: 'Models',
            accessor: () => null,
            width: '18%',
            render: (_value, row, rowIndex) => (
                <div id={`result-container-${rowIndex}`} className="tw-flex tw-flex-col tw-w-full">
                    <ResultsList row={row} rowIndex={rowIndex} />
                </div>
            ),
        },
        {
            id: 'started_at',
            header: 'Start date',
            accessor: (r) => convertToDateMonthYear(r?.started_at ?? ''),
            cellType: 'meta',
            width: '10%',
            render: (value, _row, rowIndex) => <span id={`start-date-${rowIndex}`}>{value}</span>,
        },
        {
            id: 'ended_at',
            header: 'End date',
            accessor: (r) => convertToDateMonthYear(r?.ended_at ?? ''),
            cellType: 'meta',
            width: '10%',
            render: (value, _row, rowIndex) => <span id={`end-date-${rowIndex}`}>{value}</span>,
        },
        {
            id: 'ownership',
            header: 'Updated by',
            accessor: (r) => r?.ownership?.username ?? '',
            width: '12%',
            render: (value, _row, rowIndex) => <span id={`updated-by-${rowIndex}`}>{value}</span>,
        },
        {
            id: 'actions',
            header: '',
            accessor: () => null,
            width: '48px',
            align: 'right',
            render: (_value, row, rowIndex) => renderRowActions(row, rowIndex),
        },
    ];

    return (
        <div id="ab-test-table-container" className="tw-mt-3">
            <Table
                columns={columns}
                data={abTests ?? []}
                rowKey="id"
                striped
                hoverable
                loading={loading}
                stickyHeader
                maxHeight="600px"
                id="ab-test-table"
                data-testid="ab-tests-table"
            />
        </div>
    );
}

export default ABTable;
